import { BaseMapper, Nullable } from '@ps/utils';

import {
  SurveyManagementDataDto,
  SurveyPerformancePayloadDto,
  SurveyPerformanceReportResultDto,
} from '../dtos/survey-performance-report.dto';
import { SurveyPerformanceResult } from '../../models/survey-performance-report.model';

const COMPLETE_STATUS_CODE = 21;
const VALID_CLICKS_STATUS_CODES = new Set([17, 18, 19, 21]);
const BUYER_CLICKS_STATUS_CODES = new Set([17, 18, 19, 20, 21, 30]);

export class SurveyPerformanceReportMapper extends BaseMapper<
  SurveyPerformanceReportResultDto,
  SurveyPerformanceResult
> {
  public fromDto(
    dto: SurveyPerformanceReportResultDto,
  ): SurveyPerformanceResult {
    const aggregated = this._aggregatePayload(dto.Payload);
    const mgmt = this._resolveManagement(
      dto.surveyManagementData,
      aggregated.surveyName,
    );
    const totalRevenue = this._round(aggregated.buyerRevenue);
    const totalCost = this._round(aggregated.supplierRevenue);

    return {
      surveyId: dto._id,
      surveyName: mgmt.surveyName,
      po: mgmt.po,
      buyerName: aggregated.buyerName,
      country: aggregated.country,
      cpi: mgmt.cpi,
      totalRevenue,
      totalCost,
      margin: this._round(totalRevenue - totalCost),
      completesInTimeFrame: aggregated.completesInTimeFrame,
      totalCompletes: this._resolveTotalCompletes(dto.surveyManagementData),
      completesRemaining: mgmt.completesRemaining,
      statedLoi: mgmt.statedLoi,
      statedIr: mgmt.statedIr,
      currentLoi: mgmt.currentLoi,
      currentIr: mgmt.currentIr,
      buyerClicks: aggregated.buyerClicks,
      validClicks: aggregated.validClicks,
      totalClicks: aggregated.totalClicks,
      buyerEpc: this._calculateEpc(
        aggregated.buyerRevenue,
        aggregated.buyerClicks,
      ),
      validEpc: this._calculateEpc(
        aggregated.buyerRevenue,
        aggregated.validClicks,
      ),
      totalEpc: this._calculateEpc(
        aggregated.buyerRevenue,
        aggregated.totalClicks,
      ),
    };
  }

  private _resolveManagement(
    management: SurveyManagementDataDto | undefined,
    fallbackSurveyName: string,
  ): {
    surveyName: string;
    po: string;
    cpi: number;
    completesRemaining: number;
    statedLoi: number;
    statedIr: number;
    currentLoi: number;
    currentIr: Nullable<number>;
  } {
    if (!management) {
      return {
        surveyName: fallbackSurveyName,
        po: '',
        cpi: 0,
        completesRemaining: 0,
        statedLoi: 0,
        statedIr: 0,
        currentLoi: 0,
        currentIr: null,
      };
    }
    return {
      surveyName: management.tle,
      po: management.survey_po,
      cpi: this._round(management.cpi_current),
      completesRemaining: management.goal - management.fielded,
      statedLoi: Math.round(management.loi_launch),
      statedIr: Math.round(management.incidence_launch),
      currentLoi: this._round(management.gpp?.LOI ?? 0),
      currentIr: this._round(management.gpp?.IR ?? 0),
    };
  }

  private _calculateEpc(revenue: number, clicks: number): number {
    return clicks > 0 ? this._round(revenue / clicks) : 0;
  }

  private _aggregatePayload(payloads: SurveyPerformancePayloadDto[]): {
    buyerRevenue: number;
    supplierRevenue: number;
    completesInTimeFrame: number;
    validClicks: number;
    buyerClicks: number;
    totalClicks: number;
    surveyName: string;
    buyerName: string;
    country: string;
  } {
    let buyerRevenue = 0;
    let supplierRevenue = 0;
    let completesInTimeFrame = 0;
    let validClicks = 0;
    let buyerClicks = 0;
    let totalClicks = 0;
    let surveyName = '';
    let buyerName = '';
    let country = '';

    for (const payload of payloads) {
      if (payload.statusCode === COMPLETE_STATUS_CODE) {
        buyerRevenue = payload.buyerRevenue;
        supplierRevenue = payload.supplierRevenue;
        completesInTimeFrame = payload.totalOfTimeFrame;
      }
      if (VALID_CLICKS_STATUS_CODES.has(payload.statusCode)) {
        validClicks += payload.total;
      }
      if (BUYER_CLICKS_STATUS_CODES.has(payload.statusCode)) {
        buyerClicks += payload.total;
      }
      totalClicks += payload.total;
      surveyName = payload.surveyName;
      buyerName = payload.bName;
      country = payload.country;
    }

    return {
      buyerRevenue,
      supplierRevenue,
      completesInTimeFrame,
      validClicks,
      buyerClicks,
      totalClicks,
      surveyName,
      buyerName,
      country,
    };
  }

  private _resolveTotalCompletes(
    management: SurveyManagementDataDto | undefined,
  ): number {
    if (!management) return 0;

    const buyerFieldedAfterAdj =
      management.statistics?.[0]?.buyer?.completes?.buyer_fielded_after_adj;

    return buyerFieldedAfterAdj ?? management.fielded;
  }

  private _round(value: number): number {
    return Math.round(value * 100) / 100;
  }
}
